import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, catchError, map } from 'rxjs';
import { ErrorMessage } from '../../shared/models/error-message.enum';
import { PasswordRecoveryService } from '../password-recovery/services/password-recovery.service';

@Injectable({
  providedIn: 'root',
})
export class PasswordResetGuard {
  constructor(
    private passRecoveryService: PasswordRecoveryService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const token = route.paramMap.get('passwordResetToken') ?? '';
    const username = route.paramMap.get('username') ?? '';

    return this.passRecoveryService.checkResetTokenValid(username, token).pipe(
      map((res: any) => {
        if (this.isOk(res)) {
          return true;
        }
        this.redirectToLogin();
        return false;
      }),
      catchError((error) => {
        this.notifyError(error);
        this.redirectToLogin();
        return of(false);
      }),
    );
  }

  /** Accepts HttpResponse-like objects or plain objects with status/statusCode */
  private isOk(res: any): boolean {
    const status = (res?.status ?? res?.statusCode ?? 0) as number;
    return status === HttpStatusCode.Ok;
  }

  /** Centralized navigation to avoid duplication */
  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }

  /** Show translated error via snackbar; avoids setTimeout and subscription leaks */
  private notifyError(error: any): void {
    const status = (error?.status ?? error?.statusCode) as number | undefined;
    const key = this.getMessageKey(status);
    const translated = this.translateService.instant(key);

    this.snackBar.open(translated, 'Close', {
      duration: 5000,
      panelClass: ['snackbar-error'],
    });
  }

  /** Map HTTP status to i18n message keys */
  private getMessageKey(status?: number): ErrorMessage {
    switch (status) {
      case HttpStatusCode.NotFound:
        return ErrorMessage.TOKEN_NOT_FOUND;
      case HttpStatusCode.BadRequest:
        return ErrorMessage.TOKEN_BAD_REQUEST;
      default:
        return ErrorMessage.TOKEN_INVALID;
    }
  }
}
